import { PageSaveHeader } from '../../components/PageSaveHeader';
import { useThemeSettingsNavigationGuard } from '../../hooks/useThemeSettingsNavigationGuard';
import { useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { useAdminNarrowViewport } from '../../admin-ui';
import { createThemeSettingsPageStyles } from './themeSettingsPageStyles';
import { useThemeSettingsForm } from './useThemeSettingsForm';
import { ThemeLogoSection } from './ThemeLogoSection';
import { ThemeFontSection } from './ThemeFontSection';
import { ThemeColorsSection } from './ThemeColorsSection';
import { ThemeAssistantSection } from './ThemeAssistantSection';
import { COLOR_KEY } from './themeSettingsUtils';
import { ThemePreparationStatus } from './ThemePreparationStatus';

export function ThemeSettingsPage() {
  const { tokens } = useTheme();
  const isNarrow = useAdminNarrowViewport();
  const styles = useMemo(() => createThemeSettingsPageStyles(tokens, { narrow: isNarrow }), [tokens, isNarrow]);
  const form = useThemeSettingsForm(tokens);

  const { confirmationDialog } = useThemeSettingsNavigationGuard({
    isDirty: form.isDirty, isSaving: form.isSaving, onLeave: form.abandonPendingSave,
  });

  return (
    <div style={styles.page}>
      <PageSaveHeader title="Theme Settings" isDirty={form.isDirty} isSaving={form.isSaving}
        saved={form.saved} savingStatusProvided disabled={form.isFormDisabled || form.isSaveUncertain} error={form.saveError}
        preparationStatus={
        <ThemePreparationStatus
          job={form.preparationJob}
          ready={form.isPreparationReady && !form.isDirty}
          isSaving={form.isSaving}
          inherited={form.isPreparationInherited}
          error={form.preparationError}
          dirty={form.isDirty}
          retrying={form.isPreparationRetrying}
          onRetry={() => { void form.retryPreparation(); }}
        />
        }
        onSave={() => { void form.save(); }} />
      <div style={styles.pageContent}>
        {form.saveReconciliationError && <div role="alert" style={styles.errorMessage}>
          {form.saveReconciliationError} <button type="button" onClick={form.retryLoad}>Retry load
          </button>
        </div>}

        {form.loadError && <div role="alert" style={styles.errorMessage}>
          {form.loadError} <button type="button" onClick={form.retryLoad}>Retry</button>
        </div>}
        <fieldset disabled={form.isFormDisabled} style={styles.pageInner} aria-label="Theme settings">
        <ThemeLogoSection styles={styles} disabled={form.isFormDisabled} dirty={form.dirtyFields.logo} {...form.logo} />
        <ThemeFontSection styles={styles} disabled={form.isFormDisabled} dirty={form.dirtyFields.font} {...form.font} />
        <ThemeColorsSection styles={styles} dirtyColors={form.dirtyFields.colors} {...form.colors} />
        <ThemeAssistantSection styles={styles} dirtyFields={{ avatar: form.dirtyFields.assistantAvatar, name: form.dirtyFields.assistantName, greeting: form.dirtyFields.assistantGreeting, intro: form.dirtyFields.assistantIntro }} accentColor={form.colors.colors[COLOR_KEY.accent] ?? tokens.brand.accent ?? tokens.brand.primary} disabled={form.isFormDisabled} {...form.assistant} />
        </fieldset>
      </div>
      {confirmationDialog}
    </div>
  );
}
